import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const PlaystoreFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
}: GetAddIntegrationFormDrawerContentProps) => {
  const [playstoreURL, setPlaystoreURL] = useState('');
  const [apiKeyJSON, setAPIKeyJSON] = useState<string | undefined>(undefined);
  const [descriptionInput, setDescriptionInput] = useState('');

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.Playstore,
    integrationType,
    createdByUser: userID,
    inputData: {
      playstore: {
        appURL: playstoreURL,
        serviceAccountAPIKeyJSON: apiKeyJSON,
      },
    },
    description: descriptionInput.trim(),
    onError: (error: Error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        showErrorNotification({
          contentComponent: 'The app is already connected',
          props: {},
        });
        return;
      }
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Playstore,
      });
    },
  });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Playstore,
    integrationType,
    hasValidFormFields: !!playstoreURL,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      await createIntegration();
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: IntegrationChannelType.Playstore,
      });
    },
  });

  const handlePlaystoreURLChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPlaystoreURL(event.target.value);
  };

  const handleAPIKeyJSONChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAPIKeyJSON(event.target.value);
  };

  const loading = IsLoading(status);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label="Playstore URL"
              name="playstoreURL"
              placeholder="Enter Playstore URL"
              value={playstoreURL}
              disabled={loading}
              variant="outlined"
              onChange={handlePlaystoreURLChange}
            />
            <TextField
              fullWidth
              multiline
              maxRows={8}
              label="Service Account API Key JSON (Optional)"
              name="apiKeyJSON"
              placeholder="Enter Service Account API Key JSON"
              value={apiKeyJSON}
              disabled={loading}
              variant="outlined"
              onChange={handleAPIKeyJSONChange}
              helperText="Adding API Key allows Enterpret to fetch detailed metadata associated with each review."
            />
          </Stack>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidated={isPreValidated}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={loading}
      />
    </Stack>
  );
};
